import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef } from 'react';

import { getLeadByExternalIdQueryKey } from '../../../../../../services/marketplace/generated/kubb/hooks/useGetLeadByExternalId';
import { usePostLeadByExternalIdSeen } from '../../../../../../services/marketplace/generated/kubb/hooks/usePostLeadByExternalIdSeen';
import { UnreadThreadIds } from './utils';

export const useMarkSeen = (externalId: string) => {
  const queryClient = useQueryClient();
  const { mutateAsync } = usePostLeadByExternalIdSeen();
  const sentIds = useRef(new Set<string>());

  return useCallback(
    async ({ messages, offers }: Partial<UnreadThreadIds>) => {
      const unsentMessages = (messages ?? []).filter((id) => !sentIds.current.has(id));
      const unsentOffers = (offers ?? []).filter((id) => !sentIds.current.has(id));
      if (unsentMessages.length === 0 && unsentOffers.length === 0) return;

      [...unsentMessages, ...unsentOffers].forEach((id) => sentIds.current.add(id));

      try {
        await mutateAsync({
          externalId,
          data: {
            items: {
              ...(unsentMessages.length > 0 ? { messages: unsentMessages } : {}),
              ...(unsentOffers.length > 0 ? { offers: unsentOffers } : {}),
            },
          },
        });
        await queryClient.invalidateQueries({
          queryKey: getLeadByExternalIdQueryKey({ externalId }),
        });
      } catch {
        // Swallowed; the next effect run retries anything still unseen.
      } finally {
        // Evict once settled (post-refetch) so a backend seen-reset (offer flip) gets re-marked.
        [...unsentMessages, ...unsentOffers].forEach((id) => sentIds.current.delete(id));
      }
    },
    [mutateAsync, queryClient, externalId],
  );
};
